'use client';

import { PropsWithChildren } from 'react';

import { Asset, Chain } from '@chain-registry/types';
import { ChainProvider } from '@cosmos-kit/react-lite';
import { chains, assets } from 'chain-registry';

import { COSMOS_KIT_WALLETS_INFO } from 'app/constants/wallets';
import { calculateGasPrice } from 'app/utils/cosmos.utils';

import { CHAIN_CONFIG } from 'chains/chains.constants';
import { COSMOS_CHAINS } from 'chains/cosmos/cosmos.assets';
import { CosmosChainId } from 'chains/cosmos/cosmos.types';

import { AuthStatus } from './AuthStatus';

const {chainsList, assetsList} = buildChainsList();

const wallets = Object.values(COSMOS_KIT_WALLETS_INFO)
  .map(e => e.wallets)
  .flat();

export const AppChainProvider = ({children}: PropsWithChildren) => {
  return (
    <ChainProvider
      chains={chainsList}
      wallets={wallets}
      assetLists={assetsList}
      signerOptions={{
        signingStargate: handleGasSetup as never,
        signingCosmwasm: handleGasSetup as never,
      }}
      walletConnectOptions={{
        signClient: {
          projectId: CHAIN_CONFIG.walletConnectProjectId,
        },
      }}
      logLevel="NONE">
      {children}

      <AuthStatus />
    </ChainProvider>
  );
};

function buildChainsList() {
  const assetsList = [...assets];

  const chainsList: Chain[] = Object.values(COSMOS_CHAINS).map<Chain>(chain => {
    const registryChain = chains.find(e => e.chain_id === chain.chainId);
    const rpcUrl = new URL(chain.rpcURL);
    const item: Chain = {
      status: 'live',
      network_type: chain.chainNetwork === 'mainnet' ? 'mainnet' : 'testnet',
      chain_name: chain.chainId,
      chain_id: chain.chainId,
      bech32_prefix: chain.addressPrefix,
      slip44: chain.bip44?.coinType ?? registryChain?.slip44 ?? 118,
      pretty_name: chain.chainName,
      explorers: [
        {
          url: chain.explorerURL.transaction,
          tx_page: chain.explorerURL.transaction,
        },
      ],
      fees: {
        fee_tokens: chain.currencies
          .filter(e => e.isFeeCurrency)
          .map(e => ({
            denom: e.coinDenom,
          })),
      },
      key_algos: ['secp256k1'],

      ...registryChain,

      apis: {
        grpc: [
          {
            address: rpcUrl.host,
            provider: 'Custom',
          },
        ],
        rpc: [
          {
            address: chain.rpcURL,
            provider: 'Custom',
          },
        ],
        rest: [
          {
            address: chain.restURL,
            provider: 'Custom',
          },
        ],
      },
      staking: {
        staking_tokens: chain.currencies
          .filter(e => e.isStakeCurrency)
          .map(e => ({denom: e.coinDenom})),
      },
    } as never;

    const asset = assets.find(e => e.chain_name === item.chain_name);

    if (!asset) {
      const currencies = chain.currencies.map(e => {
        return {
          denom_units: [
            {
              denom: e.coinMinimalDenom,
              exponent: e.coinDecimals,
            },
          ],
          base: e.coinMinimalDenom,
          name: e.coinDenom,
          display: e.coinMinimalDenom,
          symbol: e.coinDenom,
        };
      });

      assetsList.push({
        chain_name: item.chain_name,
        assets: currencies as Asset[],
      });
    }

    return item;
  });

  return {chainsList, assetsList};
}

function handleGasSetup(chain: Chain | string) {
  if (typeof chain === 'string') {
    throw new Error('Chain is string');
  }

  return {
    gasPrice: calculateGasPrice(chain.chain_id as CosmosChainId),
  };
}
